/**
 * Route -> analytics screen name registry.
 *
 * Two rules govern this file:
 *
 * 1. ORDER MIRRORS App.js. The list is matched top-down exactly like the
 *    <Switch> in App.js, so the screen we report is the screen the user is
 *    actually looking at. When you add/move/remove a <Route> in App.js, do the
 *    same here — and keep the relative order. (Example of why: in App.js
 *    `/gruppi/:groupId/:postId` is declared BEFORE `/gruppi/:groupId/nuovo-post`,
 *    so the post-comments route wins for both; this list reproduces that.)
 *
 * 2. NAMES ARE STABLE AND LOW-CARDINALITY. Never report a raw pathname:
 *    `/ricette/8412` would create one screen per recipe and make the console's
 *    Screens report useless. Route params collapse into a single name
 *    (`recipe_detail`) — the identity of the recipe belongs in a custom event,
 *    not in the screen name.
 */
import { matchPath } from "react-router";

/** @type {{path: string|string[], exact?: boolean, name: string, className: string}[]} */
const SCREENS = [
  // ── Pre-login / auth ────────────────────────────────────────────────
  { path: "/", exact: true, name: "prelogin", className: "PreLoginPage" },
  { path: "/o/:origin", exact: true, name: "prelogin", className: "PreLoginPage" },
  { path: "/login", exact: true, name: "login", className: "LoginPage" },
  { path: "/login/:token", exact: true, name: "login_token", className: "GetTokenPage" },
  { path: "/impersonate/:token", exact: true, name: "login_impersonate", className: "GetTokenAdminPage" },
  { path: "/verifica-email", exact: true, name: "verify_email_link", className: "VerifyEmailLinkPage" },

  // Onboarding wizard. `:step` is a bounded 1..N integer, so it is safe to keep
  // in the name — per-step names are what makes the drop-off funnel readable.
  { path: "/impostazioni-iniziali/:step", exact: true, name: "onboarding_step", className: "InitialConfiguration" },
  { path: "/onboarding-complete", name: "onboarding_complete", className: "WeHaveFinished" },

  // ── Home / commerce ─────────────────────────────────────────────────
  { path: "/home", exact: true, name: "home_dashboard", className: "HomeDashboardPage" },
  { path: "/shopping-widget", name: "shop", className: "ShopPage" },
  { path: "/paywall", exact: true, name: "paywall", className: "PaywallPage" },
  { path: "/legal/:slug", exact: true, name: "legal_document", className: "LegalPage" },

  // ── Social ──────────────────────────────────────────────────────────
  { path: "/community", exact: true, name: "community_feed", className: "HomePage" },
  { path: "/me", exact: true, name: "social_profile_self", className: "SocialProfilePage" },
  { path: "/dieta-social-profile", exact: true, name: "social_profile_self", className: "SocialProfilePage" },
  { path: "/u/:userId", exact: true, name: "social_profile_user", className: "SocialProfilePage" },
  { path: "/followers", exact: true, name: "followers", className: "FollowersPage" },
  { path: "/following", exact: true, name: "following", className: "FollowingPage" },
  { path: "/impostazioni-profilo", exact: true, name: "profile_settings", className: "ProfileSettingsPage" },
  { path: "/utenti-bloccati", exact: true, name: "blocked_users", className: "BlockedUsersPage" },
  { path: "/u/:userId/followers", exact: true, name: "user_followers", className: "FollowersPage" },
  { path: "/u/:userId/following", exact: true, name: "user_following", className: "FollowingPage" },

  { path: "/corso/modulo/:lessonId", exact: true, name: "course_module_intro", className: "ModuleIntroPage" },
  { path: "/screenlock", exact: true, name: "screenlock", className: "ScreenlockPage" },

  // ── Groups & posts ──────────────────────────────────────────────────
  { path: "/gruppi", exact: true, name: "groups_list", className: "GroupsPage" },
  { path: "/gruppi/:groupId", exact: true, name: "group_detail", className: "GroupPage" },
  // Declared before `/gruppi/:groupId/nuovo-post` in App.js — order preserved.
  { path: "/gruppi/:groupId/:postId", exact: true, name: "group_post_comments", className: "PostCommentsDeepLink" },

  // ── Chat ────────────────────────────────────────────────────────────
  { path: "/chat", exact: true, name: "chat_list", className: "ChatPage" },
  { path: "/chat/free", exact: true, name: "chat_free_course", className: "ChatPage" },
  { path: "/chat/supporto/:supportType", exact: true, name: "chat_support", className: "ChatPage" },
  { path: "/chat/user/:userId", exact: true, name: "chat_direct", className: "ChatPage" },

  { path: "/nuovo-post", exact: true, name: "post_create", className: "AddPostPage" },
  { path: "/gruppi/:groupId/nuovo-post", exact: true, name: "group_post_create", className: "AddPostPage" },
  { path: "/gruppi/:groupId/:postId/:commentId", exact: true, name: "group_comment_thread", className: "CommentThreadDeepLink" },

  // ── Video / live ────────────────────────────────────────────────────
  { path: "/multimedia/preferiti", exact: true, name: "videos_favourites", className: "VideoPreferitiPage" },
  { path: "/multimedia/categoria/:categoryId", exact: true, name: "videos_category", className: "VideoCategoryPage" },
  { path: "/multimedia", exact: true, name: "videos_home", className: "MultimediaPage" },
  { path: "/video/:videoId", exact: true, name: "video_detail", className: "VideoDetailPage" },
  { path: "/dirette/:eventId", exact: true, name: "live_event_detail", className: "StreamingEventDetailPage" },

  // ── Course ──────────────────────────────────────────────────────────
  { path: "/corso", exact: true, name: "course_overview", className: "CourseOverviewPage" },
  { path: "/corso/tabs", exact: true, name: "course_tabs", className: "CourseTabsPage" },
  { path: "/corso/riprendi", exact: true, name: "course_resume", className: "CourseResumePage" },
  { path: "/corso/lezione/:lessonId", exact: true, name: "course_lesson", className: "LessonPage" },
  { path: "/corso/lezioni/:lessonId", exact: true, name: "course_lessons_list", className: "LessonsListPage" },

  // ── Profile / help ──────────────────────────────────────────────────
  { path: "/profilo", exact: true, name: "profile", className: "ProfilePage" },
  { path: "/percorso-gratuito", exact: true, name: "free_route_start", className: "StartFreeRoutePage" },
  { path: "/prodotti", exact: true, name: "products", className: "ProductsPage" },
  { path: "/profilo/*", exact: true, name: "profile_section", className: "ProfilePage" },
  { path: "/aiuto/che-cos-e-dieta-social-app", exact: true, name: "help_what_is", className: "WhatIsPage" },
  { path: "/aiuto/come-utilizzare-dieta-social-app", exact: true, name: "help_how_to_use", className: "HowToUsePage" },
  { path: "/aiuto/come-funziona-la-prova-gratuita", exact: true, name: "help_how_trial_works", className: "HowTrialWorksPage" },

  // ── Payments ────────────────────────────────────────────────────────
  { path: "/pagamento", exact: true, name: "payment", className: "PaymentsPage" },
  { path: "/metodo-di-pagamento", exact: true, name: "payment_method", className: "PaymentsPage" },
  { path: "/metodo-di-pagamento/processando", exact: true, name: "payment_method_processing", className: "PaymentMethodProcessingPage" },
  { path: "/pagamento/successo", exact: true, name: "payment_success", className: "PaymentSuccessPage" },
  { path: "/pagamento/processando", exact: true, name: "payment_processing", className: "PaymentProcessingPage" },

  { path: "/substitution/:type/:cycleId/:itemId", exact: true, name: "substitution", className: "SubstitutionPage" },
  { path: "/progressi", exact: true, name: "progress", className: "ProgressPage" },

  // ── Diary / percorso (current route + two legacy aliases, same screens) ──
  { path: "/il-tuo-percorso/storico", exact: true, name: "percorso_history", className: "MioPercorsoStoricoPage" },
  { path: "/il-tuo-percorso/prima-dopo", exact: true, name: "prima_dopo_create", className: "PrimaDopoCreatePage" },
  { path: "/il-tuo-percorso/:measureSlug", exact: true, name: "diary_measure", className: "DiaryMeasurePage" },
  { path: "/il-tuo-percorso", exact: true, name: "diary", className: "DiaryPage" },
  { path: "/misure-e-progressi", exact: true, name: "diary_measures", className: "DiaryPage" },
  { path: "/preferenze-nutrizionali", exact: true, name: "diary_nutrition_prefs", className: "DiaryPage" },
  { path: "/il-mio-percorso/storico", exact: true, name: "percorso_history", className: "MioPercorsoStoricoPage" },
  { path: "/il-mio-percorso/prima-dopo", exact: true, name: "prima_dopo_create", className: "PrimaDopoCreatePage" },
  { path: "/il-mio-percorso/:measureSlug", exact: true, name: "diary_measure", className: "DiaryMeasurePage" },
  { path: "/il-mio-percorso", exact: true, name: "diary", className: "DiaryPage" },
  { path: "/diario/:measureSlug", exact: true, name: "diary_measure", className: "DiaryMeasurePage" },
  { path: "/diario", exact: true, name: "diary", className: "DiaryPage" },

  // ── Menu & recipes ──────────────────────────────────────────────────
  // Non-exact in App.js, so this also absorbs the `/:cycleId` variant below it.
  { path: "/menu/:day/:food/:recipeId", name: "menu_recipe", className: "RecipeMenuPage" },
  { path: "/menu/:day", exact: true, name: "menu_day", className: "MenuPage" },
  { path: "/menu", exact: true, name: "menu", className: "MenuPage" },
  { path: "/cibi-non-graditi", exact: true, name: "disliked_foods", className: "CibiNonGraditiPage" },
  { path: "/sfide", exact: true, name: "challenges", className: "ChallengesPage" },
  { path: "/ricette", exact: true, name: "recipes", className: "RecipesPage" },
  { path: "/ricette/:recipeId", exact: true, name: "recipe_detail", className: "RecipePage" },
  { path: "/ricette/:recipeId/:cycleId", exact: true, name: "recipe_detail", className: "RecipePage" },

  // ── Notifications & coins ───────────────────────────────────────────
  { path: "/imposta-notifiche", exact: true, name: "notifications_settings", className: "NotificationsConfigPage" },
  { path: "/notifiche", exact: true, name: "notifications", className: "NotificationsPage" },
  { path: "/saldo-monete", exact: true, name: "coins_balance", className: "CoinsBalancePage" },
  { path: "/storico-monete", exact: true, name: "coins_history", className: "CoinsHistoryPage" },
  { path: "/regole-monete", exact: true, name: "coins_rules", className: "CoinsRulesPage" },

  // ── Area Team (staff) ───────────────────────────────────────────────
  { path: "/area-team/posts", exact: true, name: "team_posts", className: "AreaTeamPostsPage" },
  { path: "/area-team/crea-post/:postId", exact: true, name: "team_post_edit", className: "AreaTeamCreatePostsPage" },
  { path: "/area-team/crea-post", exact: true, name: "team_post_create", className: "AreaTeamCreatePostsPage" },
  { path: "/area-team/gestione-utenti", exact: true, name: "team_users", className: "AreaTeamGestioneUtentiPage" },
  { path: "/area-team/chat-supporto/:cid", exact: true, name: "team_support_chat_detail", className: "AreaTeamChatSupportoDetailPage" },
  { path: "/area-team/chat-supporto", exact: true, name: "team_support_chat", className: "AreaTeamChatSupportoPage" },
  { path: "/area-team/percorso-free", exact: true, name: "team_free_program", className: "AreaTeamPercorsoFreePage" },

  { path: "/logout", exact: true, name: "logout", className: "LogoutRoute" },
];

/** App.js ends with a catch-all <Route component={NotFoundPage} />. */
const NOT_FOUND = { name: "not_found", className: "NotFoundPage" };

/**
 * Resolve a pathname to the screen the router will render.
 *
 * @param {string} pathname
 * @returns {{screenName: string, screenClass: string}}
 */
export function resolveScreen(pathname) {
  if (!pathname) return { screenName: NOT_FOUND.name, screenClass: NOT_FOUND.className };

  for (const screen of SCREENS) {
    const match = matchPath(pathname, {
      path: screen.path,
      exact: screen.exact === true,
    });
    if (!match) continue;

    return {
      screenName: decorate(screen, match.params),
      screenClass: screen.className,
    };
  }

  return { screenName: NOT_FOUND.name, screenClass: NOT_FOUND.className };
}

/**
 * The only place a route param is allowed into the screen name: the onboarding
 * step number, which is a small fixed set and the reason the funnel is legible.
 * Anything unexpected falls back to the bare name.
 */
function decorate(screen, params) {
  if (screen.name !== "onboarding_step") return screen.name;
  const step = Number(params?.step);
  if (!Number.isInteger(step) || step < 1 || step > 99) return screen.name;
  return `onboarding_step_${step}`;
}

export const __TEST_ONLY__ = { SCREENS };
